import { RefObject, useCallback, useEffect, useRef, useState } from "react"

type UsePictureInPictureArgs = {
  /** Register Chrome's automatic entry (Media Session action); the preference decides when. */
  autoEnter?: boolean
  videoRef: RefObject<HTMLVideoElement | null>
  /** Register the Media Session handler Chrome uses for automatic picture-in-picture. */
  enabled: boolean
  /** Keep the floating call visible throughout a local screen share. */
  isSharingScreen?: boolean
  /** Respect preferences that only allow manual or tab-switch entry. */
  autoEnterOnShare?: boolean
}

export type PictureInPictureFailure = "unsupported" | "no-video" | "rejected"
export type PictureInPictureResult = { ok: true } | { ok: false; reason: PictureInPictureFailure }

export type PictureInPictureState = {
  /** Some form of picture-in-picture is available in this browser. */
  supported: boolean
  /** Document picture-in-picture: a floating window that renders our own call UI. */
  documentSupported: boolean
  /** Chrome's automatic (tab-switch) PiP can be armed in this browser. */
  autoSupported: boolean
  /** The call is currently shown in a PiP window. */
  active: boolean
  /** The open document picture-in-picture window, for rendering the compact call into it. */
  pipWindow: Window | null
  enter: () => Promise<PictureInPictureResult>
  exit: () => Promise<void>
}

export const PIP_WINDOW_WIDTH = 480
export const PIP_WINDOW_HEIGHT = 400
/** Focus changes this soon after the floating window opens are part of opening it, not the attendee leaving. */
export const PIP_FOCUS_SETTLE_MS = 1500

export const isDocumentPictureInPictureSupported = () =>
  typeof window !== "undefined" &&
  typeof (window as any).documentPictureInPicture?.requestWindow === "function"

export const isVideoPictureInPictureSupported = () =>
  typeof document !== "undefined" &&
  "pictureInPictureEnabled" in document &&
  Boolean((document as any).pictureInPictureEnabled) &&
  typeof HTMLVideoElement !== "undefined" &&
  typeof (HTMLVideoElement.prototype as any).requestPictureInPicture === "function"

export const isAutoPictureInPictureSupported = () =>
  typeof navigator !== "undefined" &&
  "mediaSession" in navigator &&
  typeof (navigator as any).mediaSession?.setActionHandler === "function" &&
  (isDocumentPictureInPictureSupported() || isVideoPictureInPictureSupported())

/**
 * Copies the opener's style sheets into a document picture-in-picture window so the compact
 * call renders with the app's styles, then pins the window body to a dark full-height layout.
 */
export const prepareDocumentPictureInPictureWindow = (source: Document, target: Document) => {
  Array.from(source.styleSheets).forEach((sheet) => {
    try {
      const rules = Array.from(sheet.cssRules).map((rule) => rule.cssText).join("")
      const style = target.createElement("style")
      style.textContent = rules
      target.head.appendChild(style)
    } catch {
      // Cross-origin sheets (fonts) cannot be read; link them instead.
      if (!sheet.href) return
      const link = target.createElement("link")
      link.rel = "stylesheet"
      link.type = sheet.type
      link.media = sheet.media.mediaText
      link.href = sheet.href
      target.head.appendChild(link)
    }
  })
  const base = target.createElement("style")
  base.textContent = "html,body{height:100%;margin:0;overflow:hidden;background:#0f1419;color:#fff;color-scheme:dark}"
  target.head.appendChild(base)
  target.title = "WiseFriend call"
}

/**
 * Picture-in-picture for the custom surface (no per-meeting setting; always offered where the
 * browser supports it):
 *
 * - Document picture-in-picture (Chrome, Edge, Firefox 151+): opens a floating window and
 *   reports it through `pipWindow` so the surface can render the compact call (video or avatar,
 *   name, mic/camera/leave controls) into it. Works with the camera off and when the attendee is
 *   alone.
 * - Video picture-in-picture (Safari and other browsers without the document API): puts the
 *   current stage video into the browser's PiP player. Needs a video that is actually playing.
 * - Automatic (Chrome): registers the Media Session `enterpictureinpicture` action. Chrome
 *   invokes it on tab switch when the page is capturing the microphone or camera and the site's
 *   "Automatic picture-in-picture" setting allows it. Minimizing the browser or switching apps
 *   does not trigger it, and Chrome closes the automatic window when the tab is visible again.
 *
 * All of these are progressive enhancements: unsupported browsers keep a fully usable call.
 */
export function usePictureInPicture({ videoRef, enabled, autoEnter = true, isSharingScreen = false, autoEnterOnShare = true }: UsePictureInPictureArgs): PictureInPictureState {
  const documentSupported = isDocumentPictureInPictureSupported()
  const videoSupported = isVideoPictureInPictureSupported()
  const supported = documentSupported || videoSupported
  const autoSupported = isAutoPictureInPictureSupported()
  const [active, setActive] = useState(false)
  const [pipWindow, setPipWindow] = useState<Window | null>(null)
  const pipWindowRef = useRef<Window | null>(null)
  const pendingEntryRef = useRef<Promise<PictureInPictureResult> | null>(null)
  const entryVersionRef = useRef(0)
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const sharingRef = useRef(isSharingScreen)
  sharingRef.current = isSharingScreen
  const previousSharingRef = useRef(false)

  const closeDocumentWindow = useCallback(() => {
    const current = pipWindowRef.current
    pipWindowRef.current = null
    setPipWindow(null)
    setActive(false)
    if (current) {
      try {
        current.close()
      } catch {
        // The window may already be gone.
      }
    }
  }, [])

  const enter = useCallback((): Promise<PictureInPictureResult> => {
    if (!enabledRef.current) return Promise.resolve({ ok: false, reason: "rejected" })
    if (pendingEntryRef.current) return pendingEntryRef.current
    if (pipWindowRef.current || document.pictureInPictureElement) return Promise.resolve({ ok: true })
    const version = entryVersionRef.current
    const startedHidden = document.visibilityState === "hidden"
    let wasHidden = startedHidden
    let returnedToTab = false
    const trackPendingVisibility = () => {
      if (document.visibilityState === "hidden") wasHidden = true
      else if (wasHidden) returnedToTab = true
    }
    document.addEventListener("visibilitychange", trackPendingVisibility)
    const cancelled = () => !enabledRef.current || version !== entryVersionRef.current || (!sharingRef.current && (returnedToTab || (startedHidden && document.visibilityState === "visible")))
    const request = async (): Promise<PictureInPictureResult> => {
      if (documentSupported) {
        let opened: Window
        try {
          // Must be the first async step so the user gesture (or Chrome's automatic action) still applies.
          opened = await (window as any).documentPictureInPicture.requestWindow({
            width: PIP_WINDOW_WIDTH,
            height: PIP_WINDOW_HEIGHT,
          })
        } catch {
          return { ok: false, reason: "rejected" }
        }
        if (!opened?.document) return { ok: false, reason: "rejected" }
        if (cancelled()) {
          opened.close()
          return { ok: false, reason: "rejected" }
        }
        prepareDocumentPictureInPictureWindow(document, opened.document)
        pipWindowRef.current = opened
        setPipWindow(opened)
        setActive(true)
        opened.addEventListener(
          "pagehide",
          () => {
            if (pipWindowRef.current !== opened) return
            pipWindowRef.current = null
            setPipWindow(null)
            setActive(false)
          },
          { once: true },
        )
        return { ok: true }
      }
      if (!videoSupported) return { ok: false, reason: "unsupported" }
      const video = videoRef.current
      // HAVE_METADATA (1) is the minimum the browser accepts; below that the request rejects.
      if (!video || video.readyState < 1) return { ok: false, reason: "no-video" }
      try {
        await (video as any).requestPictureInPicture()
        if (cancelled()) {
          if (document.pictureInPictureElement === video) await document.exitPictureInPicture()
          return { ok: false, reason: "rejected" }
        }
        setActive(true)
        return { ok: true }
      } catch {
        return { ok: false, reason: "rejected" }
      }
    }
    const pending = request().finally(() => {
      document.removeEventListener("visibilitychange", trackPendingVisibility)
      pendingEntryRef.current = null
    })
    pendingEntryRef.current = pending
    return pending
  }, [documentSupported, videoRef, videoSupported])

  const exit = useCallback(async () => {
    // Invalidate an unresolved browser request too, so Leave/Return cannot reopen PiP.
    entryVersionRef.current += 1
    if (pipWindowRef.current) {
      closeDocumentWindow()
    }
    if (typeof document === "undefined" || !(document as any).pictureInPictureElement) return
    try {
      await (document as any).exitPictureInPicture()
    } catch {
      // Nothing to do: the player may already be closed.
    } finally {
      setActive(false)
    }
  }, [closeDocumentWindow])

  useEffect(() => {
    const wasSharing = previousSharingRef.current
    previousSharingRef.current = isSharingScreen
    if (!enabled) return
    if (isSharingScreen && !wasSharing && autoEnterOnShare) void enter()
    // Browser Stop sharing and the in-call button follow the same rule. While away,
    // keep the companion until the attendee returns to the meeting tab.
    if (!isSharingScreen && wasSharing && document.visibilityState === "visible") void exit()
  }, [autoEnterOnShare, enabled, enter, exit, isSharingScreen])

  useEffect(() => {
    if (!videoSupported) return
    const handleLeave = () => setActive(false)
    const handleEnter = () => setActive(true)
    document.addEventListener("leavepictureinpicture", handleLeave, true)
    document.addEventListener("enterpictureinpicture", handleEnter, true)
    return () => {
      document.removeEventListener("leavepictureinpicture", handleLeave, true)
      document.removeEventListener("enterpictureinpicture", handleEnter, true)
    }
  }, [videoSupported])

  // The floating call stays open while sharing. Otherwise, coming back to the call page (tab
  // shown again, or its window focused after another window or the PiP window itself had focus)
  // closes it, so the call never shows twice.
  // Opening the window itself bounces focus (Chrome focuses the new window, then hands focus back
  // to the page), so window focus changes only count once the window has settled.
  useEffect(() => {
    if (!active) return
    const openedAt = Date.now()
    const settled = () => Date.now() - openedAt >= PIP_FOCUS_SETTLE_MS
    let away = document.visibilityState === "hidden"
    const markAway = () => {
      if (settled()) away = true
    }
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        away = true
        return
      }
      if (away && !sharingRef.current) void exit()
    }
    const handleFocus = () => {
      if (away && settled() && document.visibilityState === "visible" && !sharingRef.current) void exit()
    }
    document.addEventListener("visibilitychange", handleVisibility)
    window.addEventListener("blur", markAway)
    window.addEventListener("focus", handleFocus)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility)
      window.removeEventListener("blur", markAway)
      window.removeEventListener("focus", handleFocus)
    }
  }, [active, exit])

  // Close our floating window when the call ends or the surface unmounts.
  useEffect(() => {
    if (enabled) return
    void exit()
  }, [enabled, exit])
  useEffect(() => () => { void exit() }, [exit])

  useEffect(() => {
    if (!enabled || !autoSupported || !autoEnter) return
    const mediaSession = (navigator as any).mediaSession
    try {
      mediaSession.setActionHandler("enterpictureinpicture", () => {
        void enter()
      })
    } catch {
      return
    }
    return () => {
      try {
        mediaSession.setActionHandler("enterpictureinpicture", null)
      } catch {
        // Older browsers reject unknown actions.
      }
    }
  }, [autoEnter, autoSupported, enabled, enter])

  return { supported, documentSupported, autoSupported, active, pipWindow, enter, exit }
}

export const describePictureInPictureFailure = (reason: PictureInPictureFailure) => {
  switch (reason) {
    case "unsupported":
      return "Picture-in-picture is not available in this browser."
    case "no-video":
      return "Picture-in-picture needs a video on the stage in this browser. Turn on your camera or wait for someone's video, then try again."
    default:
      return "The browser did not open picture-in-picture. Try the button again."
  }
}
